/**
 * Canonical row definitions for the PQR Welding Variables matrix.
 *
 * Kept as a separate module so the mapper, editor, diff builder, and patch
 * builder all share one source of truth. `rowKey` is the stable identifier
 * used in diff payloads and `field` is the actual key inside
 * `weldDetails.groups[*].fields` — except for the synthetic `gasFlowRate`
 * row, which composes two underlying fields (`gasFlowRateMin`/`Max`).
 */
import type { CatalogCategoryKey } from '../catalog/categoryKeys'

export type WeldingVariableRowKey =
  | 'weldingProcess'
  | 'weldingPosition'
  | 'weldingDirection'
  | 'weldingSystem'
  | 'torchConfiguration'
  | 'torchSplitDistance'
  | 'fillerManufacturer'
  | 'fillerTradeName'
  | 'fillerAwsSpec'
  | 'fillerAwsClass'
  | 'fillerHeatNo'
  | 'wireDiameter'
  | 'shieldingComposition'
  | 'awsDesignator'
  | 'gasFlowRate'
  | 'gasNozzleOrifice'
  | 'ctwd'
  | 'stringOrWeave'
  | 'oscillationRate'
  | 'oscillationWidth'
  | 'dwellTime'
  | 'heatAngle'
  | 'electricalPolarity'

export interface WeldingVariableRowDef {
  rowKey: WeldingVariableRowKey
  label: string
  unit?: string
  /** Underlying field key inside `weldDetails.groups[*].fields`. Absent for composed rows. */
  field?: string
  /** Synthetic rows that split into multiple persisted fields. */
  composedFields?: string[]
}

export const WELDING_VARIABLE_ROW_DEFS: WeldingVariableRowDef[] = [
  { rowKey: 'weldingProcess', label: 'Welding Process', field: 'weldingProcess' },
  { rowKey: 'weldingPosition', label: 'Welding Position', field: 'weldingPosition' },
  { rowKey: 'weldingDirection', label: 'Welding Direction', field: 'weldingDirection' },
  { rowKey: 'weldingSystem', label: 'Welding System', field: 'weldingSystem' },
  { rowKey: 'torchConfiguration', label: 'Torch Configuration', field: 'torchConfiguration' },
  { rowKey: 'torchSplitDistance', label: 'Torch Split Distance', unit: '(in)', field: 'torchSplitDistance' },
  { rowKey: 'fillerManufacturer', label: 'Filler Metal Manufacturer', field: 'fillerManufacturer' },
  { rowKey: 'fillerTradeName', label: 'Filler Metal Trade Name', field: 'fillerTradeName' },
  { rowKey: 'fillerAwsSpec', label: 'Filler Metal AWS Specification', field: 'fillerAwsSpec' },
  { rowKey: 'fillerAwsClass', label: 'Filler Metal AWS Classification', field: 'fillerAwsClass' },
  { rowKey: 'fillerHeatNo', label: 'Filler Metal Batch/Heat No.', field: 'fillerHeatNo' },
  { rowKey: 'wireDiameter', label: 'El./Wire Diameter', unit: '(in)', field: 'wireDiameter' },
  { rowKey: 'shieldingComposition', label: 'Shielding Composition', unit: '(%)', field: 'shieldingComposition' },
  { rowKey: 'awsDesignator', label: 'AWS Designator', unit: '(A5.32)', field: 'awsDesignator' },
  {
    rowKey: 'gasFlowRate',
    label: 'Gas Flow Rate',
    unit: '(cfh)',
    composedFields: ['gasFlowRateMin', 'gasFlowRateMax']
  },
  { rowKey: 'gasNozzleOrifice', label: 'Gas Nozzle Orifice Size', unit: '(in)', field: 'gasNozzleOrifice' },
  { rowKey: 'ctwd', label: 'C.T.W.D', unit: '(in)', field: 'ctwd' },
  { rowKey: 'stringOrWeave', label: 'String or Weave', field: 'stringOrWeave' },
  {
    rowKey: 'oscillationRate',
    label: 'Oscillation Rate',
    unit: '(BPM)',
    composedFields: ['oscillationRateMin', 'oscillationRateMax']
  },
  {
    rowKey: 'oscillationWidth',
    label: 'Oscillation Width',
    unit: '(in)',
    composedFields: ['oscillationWidthMin', 'oscillationWidthMax']
  },
  {
    rowKey: 'dwellTime',
    label: 'Dwell Time',
    unit: '(s)',
    composedFields: ['dwellTimeMin', 'dwellTimeMax']
  },
  { rowKey: 'heatAngle', label: 'Head Angle', unit: '(°)', field: 'heatAngle' },
  { rowKey: 'electricalPolarity', label: 'Electrical Current/Polarity', field: 'electricalPolarity' }
]

export function findWeldingVariableRowDef(rowKey: WeldingVariableRowKey): WeldingVariableRowDef {
  const def = WELDING_VARIABLE_ROW_DEFS.find((d) => d.rowKey === rowKey)
  if (!def) {
    throw new Error(`Unknown welding variable rowKey: ${rowKey}`)
  }
  return def
}

/**
 * Catalog category for each welding-variable row, where one exists. Mirrors
 * `WELD_FIELD_CATALOG` in `client/src/components/wps/WpsWeldDetails.tsx`
 * (kept in sync — adding a new row that becomes catalog-backed there must
 * also be added here). Rows without an entry are intentionally free-text:
 *
 *   - `gasFlowRate`, `oscillationRate`, `oscillationWidth`, and `dwellTime`
 *     are composed numeric min/max ranges (no catalog).
 *   - `gasNozzleOrifice` is a free-text dimension on the source form.
 *
 * Surfacing these via the same picker the WPS form uses means the PQR
 * preview gets the exact dropdown UX users already have (incl. inline
 * "add to catalog"); they don't lose any options when editing the report.
 */
export const WELDING_VARIABLE_ROW_CATALOG: Partial<
  Record<WeldingVariableRowKey, CatalogCategoryKey>
> = {
  weldingProcess: 'weld_welding_process',
  weldingPosition: 'weld_welding_position',
  weldingDirection: 'weld_welding_direction',
  weldingSystem: 'weld_welding_system',
  torchConfiguration: 'weld_torch_configuration',
  torchSplitDistance: 'weld_torch_split_distance',
  fillerManufacturer: 'weld_filler_manufacturer',
  fillerTradeName: 'weld_filler_trade_name',
  fillerAwsSpec: 'weld_filler_aws_spec',
  fillerAwsClass: 'weld_filler_aws_class',
  fillerHeatNo: 'weld_filler_heat_no',
  wireDiameter: 'weld_wire_diameter',
  shieldingComposition: 'weld_shielding_composition',
  awsDesignator: 'weld_aws_designator',
  ctwd: 'weld_ctwd',
  stringOrWeave: 'weld_string_or_weave',
  heatAngle: 'weld_heat_angle',
  electricalPolarity: 'weld_electrical_polarity'
}

/** Compose a display/range string from paired min/max weld-detail fields. */
export function composeMinMaxFieldValues(
  fields: Record<string, string>,
  minField: string,
  maxField: string,
  legacyField?: string
): { value: string; min: string; max: string } {
  let min = (fields[minField] ?? '').trim()
  let max = (fields[maxField] ?? '').trim()
  if (!min && !max && legacyField) {
    const legacy = (fields[legacyField] ?? '').trim()
    if (legacy) {
      if (/^n\/a$/i.test(legacy)) {
        min = 'N/A'
      } else {
        const dash = legacy.indexOf('-')
        if (dash > 0) {
          min = legacy.slice(0, dash).trim()
          max = legacy.slice(dash + 1).trim()
        } else {
          min = legacy
          max = legacy
        }
      }
    }
  }
  const value = !min && !max ? '' : min && max && min !== max ? `${min}-${max}` : min || max
  return { value, min, max }
}

export function weldingVariableComposedFieldMap(): Map<WeldingVariableRowKey, [string, string]> {
  return new Map(
    WELDING_VARIABLE_ROW_DEFS.filter((d) => d.composedFields?.length === 2).map((d) => [
      d.rowKey,
      d.composedFields as [string, string]
    ])
  )
}
